import { useEffect } from "react";
import exceptionsStoreApi from "app/components/flow/designer/app-exceptions/exception-store-api";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getAppFQN, getGroup } from "./selectors";
import { setGroupAction, setIsAutomatedGroupAction } from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import navigateTo from "src/navigate-to";
import appListService from "../../../apps/pages/app-list/app-list-service";
import dropApplication from "app/components/flow/designer/dropApplication";
import api from "core/api/api";
import App from "app";
import growl from "app/components/common/growl";
import ModalManager from "app/components/common/modal/ModalManager";
import metaobjectConverter from "core/services/metaStoreService/metaobject-converter";
import { deleteAppPreferences } from "app/components/flow/designer/nodesPositionStorage";

// ============================================================================
// EXCEPTION UTILITIES
// ============================================================================

/**
 * Gets the count of exceptions for an application
 */
export const getExceptionsCount = async (appModel: any) => {
    try {
        const count = await exceptionsStoreApi.countExceptions(appModel);
        return count;
    } catch (error) {
        return 0;
    }
};

// ============================================================================
// AUTOMATED GROUP UTILITIES
// ============================================================================

export const getGroupByAppId = async (appId: string): Promise<{ group: any; isAutomated: boolean }> => {
    const groups = await metaStoreService.fetchCollection("GROUP");
    for (const g of groups.models) {
        if ((g.get("applications") || []).indexOf(appId) !== -1) {
            return {
                isAutomated: g.get("groupType") === "ILCDC" || g.get("isAutomatedGroup") === true,
                group: g.toJSON()
            };
        }
    }
    return { isAutomated: false, group: null };
};

/**
 * Custom hook to determine if app is part of an automated group
 */
export const useAutomatedGroup = () => {
    const dispatch = useAppDispatch();
    const appFQN = useAppSelector(getAppFQN);
    const group = useAppSelector(getGroup);
    useEffect(() => {
        const fetchGroupInfo = async () => {
            if (!appFQN) return;
            try {
                let { isAutomated, group: appGroup } = await getGroupByAppId(appFQN);
                dispatch(setIsAutomatedGroupAction(isAutomated));
                if (appGroup) {
                    dispatch(
                        setGroupAction({
                            ...appGroup,
                            metaInfoStatus: appGroup.metaInfoStatus ? { ...appGroup.metaInfoStatus } : null
                        })
                    );
                }
            } catch (error) {
                console.error("Failed to fetch group information:", error);
                dispatch(setIsAutomatedGroupAction(false));
                dispatch(setGroupAction(null));
            }
        };
        fetchGroupInfo();
    }, [appFQN, dispatch]);

    return group;
};

// ============================================================================
// NAVIGATION UTILITIES
// ============================================================================

/**
 * Navigates to the app groups list, optionally selecting the given group
 */
export const moveToAppGroups = (group?: any) => {
    if (group) {
        appListService.setSelectedGroup(group);
    }
    navigateTo.Apps();
};

// ============================================================================
// APP DELETION UTILITIES
// ============================================================================

/**
 * Deletes an application with confirmation dialog and proper cleanup
 * @param app - The application model to delete
 * @returns Promise that resolves when deletion is complete or user cancels
 */
export const deleteApp = async (app: any): Promise<void> => {
    if (!app) return;

    try {
        const model = new ModalManager();
        const appFQN = app.get("id");
        const appName = metaobjectConverter.convertFullNameToShortName(appFQN);
        const result = await dropApplication(model, appName);

        if (!result) {
            return; // User cancelled the deletion
        }

        // Trigger app deleted event
        App.vent.trigger("app:deleted", appFQN);

        // Handle exception store deletion if needed
        setTimeout(async () => {
            if (app.get("exceptionstoreName") !== null && !result.doNotDeleteExceptions) {
                try {
                    const deleteResult = await exceptionsStoreApi.deleteLogs(appFQN);
                    if (deleteResult !== true) {
                        console.warn("cannot delete logs for " + appFQN, deleteResult);
                    }
                    growl.success("ExceptionStore Deleted.", "Success", appFQN, appFQN);
                } catch (error) {
                    growl.error("There was a problem deleting the Exception Store.", "Error", appFQN, appFQN);
                }
            }

            // Drop the application
            try {
                await api.dropObject({
                    type: "application",
                    name: appName
                });
                deleteAppPreferences(appFQN);
                growl.success("Application Dropped.", "Success!", appFQN, appFQN);
            } catch (error) {
                const errorMsg =
                    "Unable to drop app! " +
                    error.message +
                    ". Resolve the errors before retry or force drop app. Contact Striim support if problem persists.";
                growl.error(errorMsg, "Error", appFQN, appFQN);
            } finally {
                navigateTo.Apps();
            }
        }, 0);
    } catch (error) {
        console.error("Delete app error:", error);
    }
};
